import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import api, { errMsg } from '../../lib/api';
import { AlertTriangle, Save } from 'lucide-react';
import SectionForm from './SectionForm';
import { StageMover, StageStepper } from './StageMover';

const TAB_CLS = 'rounded-full border border-[#E5E0EA] data-[state=active]:bg-[#5E5068] data-[state=active]:text-white text-[12px]';

const ActivityLog = ({ client, onUpdated }) => {
  const [text, setText] = useState('');
  const add = async () => {
    try {
      const r = await api.post(`/ludo/clients/${client.client_id}/notes`, { text });
      setText(''); onUpdated(r.data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-3" data-testid="ludo-activity">
      <div className="flex gap-2">
        <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a note (calls, voicemails, payer follow-ups)…" className="rounded-xl h-9" data-testid="ludo-note-input" />
        <Button disabled={!text.trim()} onClick={add} className="rounded-xl bg-[#5E5068] text-white" data-testid="ludo-note-add">Add</Button>
      </div>
      {[...(client.activity || [])].reverse().map((a, i) => (
        <div key={i} className="text-[13px] border-l-2 border-[#C9BEDA] pl-3" data-testid={`ludo-activity-${i}`}>
          <div className="text-[#2D2833]">{a.text}</div>
          <div className="text-[11px] text-[#8A7B95]">{a.by} · {new Date(a.at).toLocaleString()}</div>
        </div>
      ))}
    </div>
  );
};

const CardTop = ({ client, schema, onMoved }) => (
  <>
    <StageStepper stages={schema.stages} current={client.stage} />
    {client.alerts?.map((a) => (
      <div key={a} className="text-[13px] text-[#8A3A3A] flex items-center gap-1.5" data-testid="ludo-drawer-alert"><AlertTriangle size={14} />{a}</div>
    ))}
    {client.next_stage && client.missing_for_next.length > 0 && (
      <div className="text-[12px] text-[#6E5F78]" data-testid="ludo-drawer-next-missing">
        Before <b>{client.next_stage}</b>: {client.missing_for_next.join(' · ')}
      </div>
    )}
    <StageMover key={client.stage} client={client} schema={schema} onMoved={onMoved} />
  </>
);

// One client card: every section is a tab on the same card. clientId === 'new' opens it blank.
export const LudoClientDrawer = ({ clientId, schema, payers, onClose, onChanged, onCreated }) => {
  const isNew = clientId === 'new';
  const [client, setClient] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft({});
    if (!clientId) return;
    if (isNew) { setClient({ stage: 'Inquiry', activity: [], alerts: [], missing_for_next: [] }); return; }
    setClient(null);
    api.get(`/ludo/clients/${clientId}`).then((r) => setClient(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [clientId, isNew]);

  const updated = (c) => { setClient(c); onChanged(); };
  const setField = (sec, key, val) => setDraft((d) => ({ ...d, [sec]: { ...(d[sec] || {}), [key]: val } }));
  const merged = (sec) => ({ ...(client?.[sec] || {}), ...(draft[sec] || {}) });
  const dirty = Object.keys(draft).length > 0;

  const save = async () => {
    setSaving(true);
    try {
      if (isNew) {
        const r = await api.post('/ludo/clients/full', draft);
        toast.success('Client added'); setDraft({}); onCreated(r.data.client_id);
      } else {
        const r = await api.patch(`/ludo/clients/${clientId}`, draft);
        setDraft({}); updated(r.data); toast.success('Saved');
      }
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  const close = () => {
    if (dirty && !window.confirm('Discard unsaved changes?')) return;
    onClose();
  };

  return (
    <Dialog open={!!clientId} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="ludo-client-drawer">
        {!client ? <div className="p-6 text-[#685B72]">Loading…</div> : (
          <div className="space-y-4">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl text-[#2D2833]" data-testid="ludo-drawer-name">
                {isNew ? 'New Ludo client' : (client.client_name || 'Unnamed client')}
              </DialogTitle>
            </DialogHeader>
            {isNew
              ? <p className="text-[12px] text-[#6E5F78]">Fill in what you have now — every tab can be completed later. Only the child's first name is required.</p>
              : <CardTop client={client} schema={schema} onMoved={updated} />}
            <Tabs defaultValue={schema.sections[0].key}>
              <TabsList className="flex flex-wrap h-auto gap-1 bg-transparent p-0">
                {schema.sections.map((s) => (
                  <TabsTrigger key={s.key} value={s.key} className={TAB_CLS} data-testid={`ludo-tab-${s.key}`}>
                    {s.label}{draft[s.key] ? ' •' : ''}
                  </TabsTrigger>
                ))}
                {!isNew && <TabsTrigger value="activity" className={TAB_CLS} data-testid="ludo-tab-activity">Activity</TabsTrigger>}
              </TabsList>
              {schema.sections.map((s) => (
                <TabsContent key={s.key} value={s.key} className="pt-3 min-h-[260px]">
                  <SectionForm section={s} values={merged(s.key)} payers={payers} referralSources={schema.referral_sources}
                    onChange={(k, v) => setField(s.key, k, v)} />
                </TabsContent>
              ))}
              {!isNew && <TabsContent value="activity" className="pt-3"><ActivityLog client={client} onUpdated={updated} /></TabsContent>}
            </Tabs>
            {(dirty || isNew) && (
              <div className="sticky bottom-0 bg-white/95 backdrop-blur border-t border-[#E5E0EA] py-3 flex justify-end gap-2">
                {!isNew && <Button variant="ghost" onClick={() => setDraft({})} data-testid="ludo-discard-btn">Discard</Button>}
                <Button onClick={save} disabled={saving || (isNew && !(draft.child?.first_name || '').trim())}
                  className="rounded-xl bg-[#5E5068] text-white" data-testid="ludo-save-btn">
                  <Save size={14} className="mr-1" />{isNew ? 'Add client' : 'Save changes'}
                </Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LudoClientDrawer;
